import { useCallback, useEffect, useMemo, useState } from "react";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { acquireSharedChannel } from "@/app/_module/services/sharedRealtimeChannel";
import { useRealtimeRejoin } from "@/app/_module/services/realtimeHealth";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";

export interface HierarchyCall {
  callSid: string;
  userId: string;
  agentName?: string;
  leadName?: string;
  contactName?: string;
  contactNumber?: string;
  agencyId?: string;
  duration?: number;
  dialerMode?: string;
}

/**
 * Live calls belonging to ONE agency in the hierarchy.
 *
 * `/communication/eavesdrop/active-calls` already returns calls across the viewer's whole
 * agency tree and stamps each one with `agencyId` — the same payload Agency Center uses.
 * So scoping to a node is a client-side filter and needs no new endpoint, no new
 * permission surface, and no second poll competing with the one Agency Center runs.
 *
 * Refresh strategy mirrors Agency Center deliberately: a 10s poll as the floor, plus the
 * `agency-live-calls:{agencyId}` broadcast for instant updates. The broadcast is keyed on
 * the VIEWER's agency (that's how the backend fans out up the tree), not the node being
 * inspected — so selecting a sub-agency still gets instant updates.
 */
/**
 * ONE hook owns the live-call feed for the whole Hierarchy tab.
 *
 * ⚠️ There were briefly two hooks here, each calling
 * `supabase.channel("agency-live-calls:{id}")`. supabase-js returns the SAME channel
 * instance per topic, so the second one registered handlers on an already-subscribed
 * channel — the identical mistake that made `usePresenceSubscription` throw after the
 * 2.112 upgrade. One owner, and consumers derive from its data.
 *
 * `/communication/eavesdrop/active-calls` already returns calls across the viewer's whole
 * agency tree, stamped with `agencyId` — the same payload Agency Center uses — so scoping
 * to a node is a client-side filter with no new endpoint and no extra permission surface.
 */
export function useAllLiveCalls(): HierarchyCall[] {
  const { user } = useUserContext();
  const [calls, setCalls] = useState<HierarchyCall[]>([]);
  const { rejoinToken, handleStatus } = useRealtimeRejoin("hierarchy-live-calls");

  const fetchCalls = useCallback(async () => {
    try {
      const res = await api.get<any>({
        url: getApiUrl("/communication/eavesdrop/active-calls"),
        auth: true,
      });
      const data = res?.data?.data || res?.data || [];
      const next = Array.isArray(data) ? data : [];
      // Only publish a NEW array when something actually changed. The 10s poll otherwise
      // hands back a fresh identity every tick, which recomputes the live-count memo and
      // re-renders every card in the tree — a steady tax on a tab people leave open all
      // day. Comparing call SIDs is enough: the panel re-reads duration from its own
      // ticking clock, not from this payload.
      setCalls((prev) => {
        if (prev.length === next.length) {
          const same = prev.every((c, i) => c.callSid === next[i]?.callSid);
          if (same) return prev;
        }
        return next;
      });
    } catch {
      // Silent — the poll retries. A failed fetch must not blank a panel showing a call
      // a manager is about to listen to.
    }
  }, []);

  const viewerAgencyId =
    (user?.currentAgency as any)?.agencyId || (user?.currentAgency as any)?.agency?.id;

  useEffect(() => {
    fetchCalls();
    // 10s floor + broadcast for instant updates — mirrors Agency Center deliberately.
    const poll = setInterval(fetchCalls, 10_000);

    // ⚠️ SHARED, not our own channel. Agency Center's Live Calls tab subscribes to this
    // exact topic and both are mounted at once (sibling <Tabs> roots). supabase-js hands
    // back the same channel object per topic, so two owners meant the first to unmount
    // killed realtime for the other. See sharedRealtimeChannel.ts.
    // Keyed on the VIEWER's agency: that's how the backend fans events up the tree, so
    // selecting a sub-agency still gets instant updates.
    const handle = viewerAgencyId
      ? acquireSharedChannel(
          `agency-live-calls:${viewerAgencyId}`,
          { call_answered: () => fetchCalls(), call_ended: () => fetchCalls() },
          handleStatus,
        )
      : null;

    return () => {
      clearInterval(poll);
      handle?.release();
    };
  }, [viewerAgencyId, fetchCalls, handleStatus, rejoinToken]);

  return calls;
}

/** Calls belonging to one agency node. Pure derivation — no extra request. */
export function callsForAgency(
  calls: HierarchyCall[],
  agencyId: string | undefined,
): HierarchyCall[] {
  if (!agencyId) return [];
  return calls.filter((c) => c.agencyId === agencyId);
}

/** Live-call count per agency, for the chart cards. Pure derivation. */
export function liveCountsByAgency(calls: HierarchyCall[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const c of calls) {
    if (!c.agencyId) continue;
    map.set(c.agencyId, (map.get(c.agencyId) ?? 0) + 1);
  }
  return map;
}
